import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus, ChevronRight, ArrowRight, Package } from "lucide-react";
import { Section, SectionHeader } from "./shared";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/hooks/useLanguage";
import { uiT } from "@/i18n/ui";
import type { SolutionData } from "@/data/solutionsData";

interface Props {
  solution: SolutionData;
  onCtaClick: () => void;
}

const SolutionOfferings = ({ solution, onCtaClick }: Props) => {
  const [openIndex, setOpenIndex] = useState(0);
  const lang = useLanguage();
  const t = uiT(lang);

  if (!solution.offerings || solution.offerings.length === 0) return null;

  return (
    <Section>
      <SectionHeader
        label={solution.offeringsLabel || t("whatWeDeliver")}
        title={solution.offeringsTitle || t("ourIntegratedOfferings")}
        icon={Package}
      />
      <p className="text-muted-foreground text-center max-w-2xl mx-auto mb-10 leading-relaxed">
        {solution.offeringsDescription || t("ourIntegratedOfferingsDesc")}
      </p>

      <div className="max-w-4xl mx-auto space-y-3">
        {solution.offerings.map((offering, i) => {
          const isOpen = openIndex === i;
          return (
            <motion.div
              key={i}
              className={`border rounded-xl overflow-hidden bg-card transition-colors duration-200 ${isOpen ? 'border-primary/30 shadow-sm' : 'border-border'}`}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3, delay: i * 0.05 }}
            >
              <button
                onClick={() => setOpenIndex(isOpen ? -1 : i)}
                className="w-full flex items-center gap-4 px-6 py-5 text-left hover:bg-muted/30 transition-colors"
              >
                <span className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center shrink-0 text-sm font-bold">
                  {i + 1}
                </span>
                <span className="text-base font-semibold text-foreground flex-1">{offering.title}</span>
                {isOpen ? <Minus size={18} className="text-muted-foreground shrink-0" /> : <Plus size={18} className="text-muted-foreground shrink-0" />}
              </button>
              <AnimatePresence>
                {isOpen && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25 }} className="overflow-hidden">
                    <div className="px-6 pb-6 pt-1">
                      <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2.5 pl-12">
                        {offering.items.map((item, j) => (
                          <motion.div
                            key={j}
                            className="flex items-start gap-2.5 group"
                            initial={{ opacity: 0, x: -8 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.2, delay: j * 0.03 }}
                          >
                            <ChevronRight size={14} className="text-primary shrink-0 mt-1 group-hover:translate-x-0.5 transition-transform" />
                            <span className="text-sm text-muted-foreground leading-relaxed">{item}</span>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}
      </div>

      {solution.offeringsClosing && (
        <p className="text-center text-sm sm:text-base text-muted-foreground max-w-3xl mx-auto mt-10 leading-relaxed italic">
          {solution.offeringsClosing}
        </p>
      )}

      <div className="text-center mt-10">
        <Button className="rounded-full px-8" onClick={onCtaClick}>
          {t("getStarted")} <ArrowRight size={16} className="ml-2" />
        </Button>
      </div>
    </Section>
  );
};

export default SolutionOfferings;
